import { z } from "zod"

export const ADDRESS_REPAIR_CONTRACT_VERSION = 1 as const

export const repairEvidenceFingerprintSchema = z.string().regex(/^[0-9a-f]{64}$/)

const normalizeProposalString = (value: string) =>
  value.normalize("NFC").replace(/\s+/gu, " ").trim()

const normalizedString = (maximum: number) =>
  z.string().transform(normalizeProposalString).pipe(z.string().min(1).max(maximum))

const normalizedNullableString = (maximum: number) => normalizedString(maximum).nullable()

const normalizedRegionNameSchema = z
  .string()
  .refine((value) => !/[\p{Cc}\p{Zl}\p{Zp}`]/u.test(value), {
    message: "region name contains a forbidden character",
  })
  .transform(normalizeProposalString)
  .pipe(z.string().min(1).max(100))

export const repairAddressSchema = z
  .object({
    streetLines: z.array(z.string().min(1).max(500)).max(10),
    city: normalizedNullableString(100),
    postcode: normalizedNullableString(32),
    region: normalizedNullableString(100),
    country: normalizedNullableString(2),
  })
  .strict()

export const carrierAddressAttributeSchema = z.enum([
  "City",
  "Street",
  "ZipCode",
  "HouseNumber",
  "ContactName",
  "ContactPhone",
  "ContactEmail",
  "CountryIsoCode",
  "MerchantReference",
  "CoD",
  "CoD.Amount",
  "CoD.Currency",
])

export const repairContextSchema = z
  .object({
    problem: z
      .object({
        kind: z.literal("shipment_suspended"),
        signature: z.string().min(1).max(300),
      })
      .strict(),
    evidence: z
      .object({
        contractVersion: z.literal(ADDRESS_REPAIR_CONTRACT_VERSION),
        repairEvidenceFingerprint: repairEvidenceFingerprintSchema,
      })
      .strict(),
    liveAddress: repairAddressSchema,
    marketplaceAddress: repairAddressSchema.nullable(),
    carrierFailure: z
      .object({
        source: z.literal("live_boxpi_probe"),
        attributes: z.array(carrierAddressAttributeSchema).max(13),
      })
      .strict()
      .nullable(),
    cityEvidence: z
      .object({
        candidates: z
          .array(
            z
              .object({
                city: z.string().min(1).max(100),
                postcode: normalizedNullableString(32),
              })
              .strict()
          )
          .max(100),
        validation: z.enum(["accepted", "rejected", "unparsed", "unavailable"]),
      })
      .strict(),
  })
  .strict()

const reasonSchema = normalizedString(300)

const replacementFieldSchema = (maximum: number) =>
  z.discriminatedUnion("status", [
    z
      .object({
        status: z.literal("unchanged"),
        value: normalizedString(maximum),
        reason: z.null(),
      })
      .strict(),
    z
      .object({
        status: z.literal("replace"),
        value: normalizedString(maximum),
        reason: reasonSchema,
      })
      .strict(),
    z.object({ status: z.literal("unresolved"), value: z.null(), reason: reasonSchema }).strict(),
  ])

const streetResolvedShape = {
  streetName: normalizedString(200),
  houseNumber: normalizedString(50),
  streetOrder: z.enum(["name_then_number", "number_then_name"]),
  deliveryInstruction: normalizedString(200).nullable(),
}

const streetProposalSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("unchanged"), ...streetResolvedShape, reason: z.null() }).strict(),
  z.object({ status: z.literal("replace"), ...streetResolvedShape, reason: reasonSchema }).strict(),
  z
    .object({
      status: z.literal("unresolved"),
      streetName: z.null(),
      houseNumber: z.null(),
      streetOrder: z.null(),
      deliveryInstruction: z.null(),
      reason: reasonSchema,
    })
    .strict(),
])

const regionProposalSchema = z.discriminatedUnion("status", [
  z
    .object({
      status: z.literal("resolved"),
      name: normalizedRegionNameSchema,
      code: z
        .string()
        .transform(normalizeProposalString)
        .transform((value) => value.toUpperCase())
        .pipe(z.string().regex(/^[A-Z0-9]{1,3}$/))
        .nullable(),
      reason: z.null().optional(),
    })
    .strict(),
  z
    .object({
      status: z.literal("unresolved"),
      name: z.null(),
      code: z.null(),
      reason: reasonSchema,
    })
    .strict(),
])

export const addressCorrectionProposalSchema = z
  .object({
    contractVersion: z.literal(ADDRESS_REPAIR_CONTRACT_VERSION),
    repairEvidenceFingerprint: repairEvidenceFingerprintSchema,
    street: streetProposalSchema,
    city: replacementFieldSchema(100),
    postcode: replacementFieldSchema(32),
    region: regionProposalSchema,
  })
  .strict()

export const addressCorrectionAttemptSchema = z
  .object({
    contractVersion: z.literal(ADDRESS_REPAIR_CONTRACT_VERSION),
    repairEvidenceFingerprint: repairEvidenceFingerprintSchema,
    modelOutput: z.string().max(8_000).optional(),
  })
  .strict()

export const ADDRESS_CORRECTION_MANUAL_REVIEW_CODE = {
  carrierAcceptsCurrentAddress: "carrier_accepts_current_address",
  unsupportedCarrierRejection: "unsupported_carrier_rejection",
  probeInconclusive: "probe_inconclusive",
  carrierFailureUnparsed: "carrier_failure_unparsed",
  validationUnavailable: "validation_unavailable",
  modelUnresolved: "model_unresolved",
  unauthorizedFieldChange: "unauthorized_field_change",
  invalidModelOutput: "invalid_model_output",
  boxpiRejected: "boxpi_rejected",
  regionUnresolved: "region_unresolved",
} as const

export const addressCorrectionManualReviewCodeSchema = z.enum(ADDRESS_CORRECTION_MANUAL_REVIEW_CODE)

export const finalCorrectionAddressSchema = repairAddressSchema.extend({
  streetLine: z.string().min(1).max(500),
  streetName: z.string().min(1).max(200),
  houseNumber: z.string().min(1).max(50),
  streetOrder: z.enum(["name_then_number", "number_then_name"]),
  city: z.string().min(1).max(100),
  postcode: z.string().min(1).max(32),
  country: z.string().length(2),
  province: z
    .object({ name: z.string().min(1).max(100), code: z.string().max(3).nullable() })
    .strict(),
  deliveryInstruction: z.string().min(1).max(200).nullable(),
})

export const structuredAddressCorrectionSchema = z.discriminatedUnion("status", [
  z
    .object({
      status: z.literal("ready_to_copy"),
      contractVersion: z.literal(ADDRESS_REPAIR_CONTRACT_VERSION),
      problemSignature: z.string().min(1).max(300),
      repairEvidenceFingerprint: repairEvidenceFingerprintSchema,
      address: finalCorrectionAddressSchema,
      // Historical records predate capture. New API writes always supply this snapshot.
      original: repairAddressSchema.optional(),
      replaced: z.array(z.enum(["street", "city", "postcode"])).max(3),
    })
    .strict(),
  z
    .object({
      status: z.literal("manual_review"),
      contractVersion: z.literal(ADDRESS_REPAIR_CONTRACT_VERSION),
      problemSignature: z.string().min(1).max(300),
      repairEvidenceFingerprint: repairEvidenceFingerprintSchema,
      code: addressCorrectionManualReviewCodeSchema,
      explanation: z.string().min(1).max(500),
      rejectedFields: z.array(carrierAddressAttributeSchema).max(13),
    })
    .strict(),
])

export type RepairAddress = z.infer<typeof repairAddressSchema>
export type RepairContext = z.infer<typeof repairContextSchema>
export type AddressCorrectionProposal = z.infer<typeof addressCorrectionProposalSchema>
export type AddressCorrectionAttempt = z.infer<typeof addressCorrectionAttemptSchema>
export type StructuredAddressCorrection = z.infer<typeof structuredAddressCorrectionSchema>
